import React from "react";
import { Link } from "react-router-dom";
import { CONTACT } from "@/lib/data";
import { Phone, MessageSquare } from "lucide-react";

const navLinks = [
  { href: "#services", label: "Services" },
  { href: "#gallery", label: "Work" },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = ({ onBookClick, topOffset = 0 }) => {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      style={{ top: `${topOffset}px` }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-28 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_mobile-detail-pro-26/artifacts/st6pxnfk_2D5BD9E6-85C3-4BF5-B85B-9DAEBA92022E.png"
            alt="DT Detail — Mobile Auto Detailing"
            className="h-16 lg:h-20 w-auto bg-white rounded-lg p-1.5 shadow-sm"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                scrolled
                  ? "text-slate-700 hover:text-[#4B0082]"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${CONTACT.phone}`}
            data-testid="navbar-call-button"
            aria-label={`Call ${CONTACT.phoneDisplay}`}
            className={`w-11 h-11 rounded-md flex items-center justify-center transition-all hover:-translate-y-0.5 ${
              scrolled
                ? "bg-slate-100 hover:bg-[#F3E8FF] text-[#4B0082] border border-slate-200"
                : "bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20"
            }`}
          >
            <Phone className="w-4 h-4" />
          </a>
          <a
            href={`sms:${CONTACT.phone}`}
            data-testid="navbar-text-button"
            aria-label={`Text ${CONTACT.phoneDisplay}`}
            className={`w-11 h-11 rounded-md flex items-center justify-center transition-all hover:-translate-y-0.5 ${
              scrolled
                ? "bg-slate-100 hover:bg-[#F3E8FF] text-[#4B0082] border border-slate-200"
                : "bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20"
            }`}
          >
            <MessageSquare className="w-4 h-4" />
          </a>
          <button
            onClick={onBookClick}
            data-testid="navbar-book-button"
            className="bg-[#4B0082] hover:bg-[#3B0066] text-white px-5 py-2.5 rounded-md font-semibold text-sm transition-all shadow-sm hover:shadow-md ml-1"
          >
            Book Now
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            <div className={`w-6 h-0.5 mb-1.5 transition-all ${scrolled ? "bg-slate-900" : "bg-white"} ${open ? "rotate-45 translate-y-2" : ""}`} />
            <div className={`w-6 h-0.5 mb-1.5 transition-all ${scrolled ? "bg-slate-900" : "bg-white"} ${open ? "opacity-0" : ""}`} />
            <div className={`w-6 h-0.5 transition-all ${scrolled ? "bg-slate-900" : "bg-white"} ${open ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-slate-700 font-medium py-2"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
